import {
  type UseMutateAsyncFunction,
  useMutation,
  type UseMutationOptions,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

type UpdateUserData = {
  isActive?: boolean | undefined;
  isSynced?: boolean | undefined;
  companyId?: UUID | undefined;
  firstName?: string | undefined;
  lastName?: string | undefined;
  password?: string | undefined;
  position?: string | undefined;
  userType?: string | undefined;
  email?: string | undefined;
  syncKey?: string | undefined;
  isVestigasSupport?: boolean | undefined; // Default: false
};

type UpdateUserPayload = {
  userData: UpdateUserData;
  userId: UUID;
};

type UpdateUserResponse = {
  code: number;
  id: UUID;
  message: string;
};

type UpdateUserMutationOptions = UseMutationOptions<
  UpdateUserResponse,
  Error,
  UpdateUserPayload
>;

export type UpdateUserMutateAsyncFunction = UseMutateAsyncFunction<
  UpdateUserResponse,
  Error,
  UpdateUserPayload
>;

/**
 * Updates a given user.
 *
 * @param {string} userId - The ID of the user to update.
 * @param {Object} featureFlags - An object containing the feature flags to update.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/update_user_vadmin_users__id__put
 */
export const updateUser = async (userId: UUID, userData: UpdateUserData) => {
  const response = await vestigasApi
    .put(ENDPOINT.USER.UPDATE(userId), {
      json: userData,
    })
    .json<UpdateUserResponse>();

  return response;
};

/**
 * Custom hook for updating a given user.
 *
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationUpdateUser = (options?: UpdateUserMutationOptions) => {
  return useMutation({
    mutationFn: async ({ userId, userData }: UpdateUserPayload) =>
      updateUser(userId, userData),

    ...options,
  });
};
